import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import { financialService } from '../../../services/api';

const FinancialSidebar = ({ 
  selectedPeriod, 
  setSelectedPeriod,
  customDateRange,
  setCustomDateRange,
  onAddTransaction,
  refreshTrigger 
}) => {
  const [showDatePicker, setShowDatePicker] = useState(selectedPeriod === 'custom');
  const [todaySummary, setTodaySummary] = useState({
    revenue: 0,
    expenses: 0,
    profit: 0
  });
  const [loadingSummary, setLoadingSummary] = useState(true);

  useEffect(() => {
    setShowDatePicker(selectedPeriod === 'custom');
  }, [selectedPeriod]);

  useEffect(() => {
    loadTodaySummary();
  }, [refreshTrigger]);

  const loadTodaySummary = async () => {
    try {
      setLoadingSummary(true);
      
      // Buscar dados de hoje - usar UTC para evitar problemas de timezone
      const today = new Date();
      const startOfDay = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));
      const endOfDay = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59));

      const params = {
        startDate: startOfDay.toISOString(),
        endDate: endOfDay.toISOString()
      };

      console.log('📊 Buscando dados de hoje com params:', params);
      const summary = await financialService.getSummary(params);
      console.log('📊 Dados de hoje recebidos:', summary);
      
      setTodaySummary({
        revenue: summary.totalIncome || 0,
        expenses: summary.totalExpense || 0,
        profit: summary.netProfit || 0
      });
    } catch (error) {
      console.error('Erro ao carregar resumo do dia:', error);
      setTodaySummary({
        revenue: 0,
        expenses: 0,
        profit: 0
      });
    } finally {
      setLoadingSummary(false);
    }
  };

  const periodOptions = [
    { value: 'currentMonth', label: 'Este Mês' },
    { value: 'lastMonth', label: 'Mês Passado' },
    { value: 'last3Months', label: 'Últimos 3 Meses' },
    { value: 'last6Months', label: 'Últimos 6 Meses' },
    { value: 'lastYear', label: 'Último Ano' },
    { value: 'custom', label: 'Período Personalizado' }
  ];

  const handlePeriodChange = (value) => {
    setSelectedPeriod(value);
    setShowDatePicker(value === 'custom');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const quickActions = [
    {
      label: 'Adicionar Despesa',
      icon: 'Plus',
      variant: 'default',
      action: () => onAddTransaction?.(),
      description: 'Registrar nova despesa'
    },
    {
      label: 'Gerar Relatório',
      icon: 'FileText',
      variant: 'secondary',
      action: () => console.log('Gerar relatório'),
      description: 'Relatório financeiro'
    },
    {
      label: 'Exportar Dados',
      icon: 'Download',
      variant: 'outline',
      action: () => console.log('Exportar dados'),
      description: 'Excel/PDF'
    }
  ];


  return (
    <div className="space-y-5">
      {/* Resumo Rápido - Movido para o topo para melhor hierarquia */}
      <div className="bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 rounded-xl p-5 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
            <Icon name="Zap" size={18} className="text-primary" />
            Hoje
          </h3>
          <button
            onClick={loadTodaySummary}
            className="p-1.5 rounded-lg hover:bg-primary/10 transition-colors"
            title="Atualizar resumo"
          >
            <Icon name="RefreshCw" size={16} className="text-primary" />
          </button>
        </div>
        
        <div className="space-y-3">
          {loadingSummary ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="flex justify-between items-center">
                  <div className="h-3 w-20 bg-primary/20 animate-pulse rounded"></div>
                  <div className="h-4 w-24 bg-primary/20 animate-pulse rounded"></div>
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className="flex justify-between items-center py-2">
                <span className="text-xs font-medium text-muted-foreground">💰 Receita</span>
                <span className="text-sm font-bold text-success">
                  {formatCurrency(todaySummary.revenue)}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-t border-primary/10">
                <span className="text-xs font-medium text-muted-foreground">💸 Despesas</span>
                <span className="text-sm font-bold text-destructive">
                  {formatCurrency(todaySummary.expenses)}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-t-2 border-primary/20">
                <span className="text-sm font-bold text-foreground">📊 Lucro</span>
                <span className={`text-lg font-bold ${todaySummary.profit >= 0 ? 'text-primary' : 'text-destructive'}`}>
                  {formatCurrency(todaySummary.profit)}
                </span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Ações Rápidas */}
      <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
        <h3 className="text-sm font-bold text-foreground mb-3 flex items-center gap-2">
          <Icon name="Zap" size={16} className="text-primary" />
          Ações Rápidas
        </h3>
        
        <div className="space-y-2">
          {quickActions.map((action, index) => (
            <button
              key={index}
              onClick={action.action}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all ${
                index === 0
                  ? 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm'
                  : 'bg-muted/50 hover:bg-muted text-foreground'
              }`}
              title={action.description}
            >
              <Icon name={action.icon} size={18} />
              <span className="text-sm font-medium">{action.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
        <h3 className="text-sm font-bold text-foreground mb-3 flex items-center gap-2">
          <Icon name="Filter" size={16} className="text-primary" />
          Filtros
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-muted-foreground mb-2 block uppercase tracking-wide">
              Período
            </label>
            <Select
              options={periodOptions}
              value={selectedPeriod}
              onChange={handlePeriodChange}
            />
          </div>
          
          {/* Data Personalizada */}
          {selectedPeriod === 'custom' && (
            <div className="space-y-3 pt-3 border-t border-border">
              <div>
                <label className="text-xs font-semibold text-muted-foreground mb-2 block">
                  Data Inicial
                </label>
                <Input
                  type="date"
                  value={customDateRange?.startDate || ''}
                  onChange={(e) => setCustomDateRange?.({ 
                    ...customDateRange, 
                    startDate: e.target.value 
                  })}
                  className="w-full"
                />
              </div>
              
              <div>
                <label className="text-xs font-semibold text-muted-foreground mb-2 block">
                  Data Final
                </label>
                <Input
                  type="date"
                  value={customDateRange?.endDate || ''}
                  onChange={(e) => setCustomDateRange?.({ 
                    ...customDateRange, 
                    endDate: e.target.value 
                  })}
                  className="w-full"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FinancialSidebar;